import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import { clearAdHocResults } from "../../../actions/restaurants";
import "./AddRestaurantCTA.css";

/**
 * CTA card encouraging users to add restaurants
 * Shows different content based on login status
 * Used at end of carousel and bottom of list views
 */
const AddRestaurantCTA = ({
  token,
  variant = "card",
  onAdHocSearch,
  adHocSearchActive,
  restaurants,
  clearAdHocResultsAction,
}) => {
  // Check if there are ad hoc results
  const hasAdHocResults = restaurants?.some(
    (r) => r.metadata?.adHocSearch === true
  );

  // Handle clear ad hoc results
  const handleClearAdHocResults = () => {
    clearAdHocResultsAction();
  };

  // Card variant (for carousel) - compact
  // Show "Discovery Search Complete" state when there are ad hoc results
  if (variant === "card" && token && !adHocSearchActive && hasAdHocResults) {
    return (
      <div className="add-restaurant-cta add-restaurant-cta-card add-restaurant-cta-card-complete">
        <div className="add-restaurant-cta-icon">
          <i className="fas fa-check-circle" />
        </div>
        <p className="add-restaurant-cta-text">
          Discovery Search Complete
        </p>
        <button
          type="button"
          onClick={handleClearAdHocResults}
          className="add-restaurant-cta-button add-restaurant-cta-button-secondary"
        >
          <i className="fas fa-eraser" /> Clear Results
        </button>
      </div>
    );
  }

  if (variant === "card") {
    return (
      <div className="add-restaurant-cta add-restaurant-cta-card">
        <div className="add-restaurant-cta-icon">
          <i className="fas fa-plus-circle" />
        </div>
        <p className="add-restaurant-cta-text">
          Don't see your favorite restaurant?
        </p>
        {token ? (
          <div className="add-restaurant-cta-buttons">
            {onAdHocSearch && (
              <button
                type="button"
                onClick={onAdHocSearch}
                className="add-restaurant-cta-button add-restaurant-cta-button-secondary"
                disabled={adHocSearchActive}
              >
                <i className="fas fa-search" /> Discovery Search
              </button>
            )}
            <Link to="/?create=true" className="add-restaurant-cta-button">
              <i className="fas fa-plus" /> Add It
            </Link>
          </div>
        ) : (
          <Link to="/signup" className="add-restaurant-cta-button">
            Sign Up to Add
          </Link>
        )}
      </div>
    );
  }

  // List variant (for half/full list views) - more detailed
  // Show "Discovery Search Complete" state when there are ad hoc results
  if (variant === "list" && token && !adHocSearchActive && hasAdHocResults) {
    return (
      <div className="add-restaurant-cta add-restaurant-cta-list add-restaurant-cta-complete">
        <div className="add-restaurant-cta-content">
          <div className="add-restaurant-cta-complete-row">
            <i className="fas fa-check-circle add-restaurant-cta-complete-icon" />
            <span className="add-restaurant-cta-complete-text">
              Discovery Search Complete
            </span>
            <button
              type="button"
              onClick={handleClearAdHocResults}
              className="add-restaurant-cta-clear-button"
            >
              <i className="fas fa-eraser" /> Clear Results
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="add-restaurant-cta add-restaurant-cta-list">
      <div className="add-restaurant-cta-content">
        <p className="add-restaurant-cta-text">
          {token ? (
            <>
              Don't see your favorite restaurant or dish?{" "}
              {onAdHocSearch ? (
                <>
                  <span className="add-restaurant-cta-tooltip-container">
                    <button
                      type="button"
                      onClick={onAdHocSearch}
                      className="add-restaurant-cta-link"
                      disabled={adHocSearchActive}
                    >
                      Try discovery search
                    </button>
                    <span className="add-restaurant-cta-tooltip">
                      Discovery search automatically finds restaurants in
                      your area and checks their menus for vegan options.
                      Great for finding hidden gems!
                    </span>
                  </span>
                  {" or "}
                </>
              ) : (
                "Please "
              )}
              <Link to="/?create=true" className="add-restaurant-cta-link">
                add it here!
              </Link>
            </>
          ) : (
            <>
              Don't see your favorite restaurant or dish? Please{" "}
              <Link to="/signup" className="add-restaurant-cta-link">
                make an account
              </Link>
              {" and add it!"}
            </>
          )}
        </p>
      </div>
    </div>
  );
};

AddRestaurantCTA.propTypes = {
  token: PropTypes.string,
  variant: PropTypes.oneOf(["card", "list"]),
  onAdHocSearch: PropTypes.func,
  adHocSearchActive: PropTypes.bool,
  restaurants: PropTypes.array,
  clearAdHocResultsAction: PropTypes.func,
};

const mapStateToProps = (state) => ({
  token: state.auth.token,
  adHocSearchActive: state.restaurant.adHocSearchActive,
  restaurants: state.restaurant.restaurants,
});

export default connect(mapStateToProps, {
  clearAdHocResultsAction: clearAdHocResults,
})(AddRestaurantCTA);
